import _ from 'lodash';
import { useMemo, useState } from 'react';
//MUI
import { Button, Collapse, Stack } from '@mui/material';
//constants
import { BankAccountRequestTableColumns } from 'constants/bank-account.constants';
import { Beneficiary, BeneficiaryRequestTableColumns, BeneficiaryStatus } from 'constants/beneficiary.constants';
import { BalanceRequestTableColumns } from 'constants/balance.constants';
import { columnType } from 'components/BasicTable/types';
// components
import { HeaderPage2 } from 'theme/elements/Headers';
import BasicTable from 'components/BasicTable';
// reducers
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { getBankListObj, getCountriesObj, getCurrenciesObj } from 'state/reducers/global.reducers';
import { getAwaitingAuthorization, getMessage } from 'state/reducers/requests.reducers';
import { selectImageUrl, selectTranslations } from 'state/reducers/translation.reducers';
import { PendingRequestTypes, RequsetPaymentTableColumn } from 'constants/requests.constants';
import { useNavigate } from 'react-router-dom';
import { pagesEnum } from 'constants/ui.constants';
import { getBeneficiaries } from 'state/reducers/beneficiary.reducers';
import { CustomAlert } from 'theme/elements/Alerts';
import { CenteredNarrowContent } from 'theme/elements/Content';
import { UserPermission } from 'constants/user.constants';
import { getUserPermission } from 'state/reducers/user.reducers';
import { EditButton, TableActionRowButton } from 'theme/elements/Buttons';
import CreateMessage from 'components/CreateMessage';
import { ReactComponent as CheckCircleOutlineIcon } from 'assets/icons/check.svg';
import { ReactComponent as HighlightOffIcon } from 'assets/icons/cancel.svg';
import WarningDialog from 'components/WarningDialog';
import { changeBeneficiaryStatus } from 'services/beneficiary.services';
import { PaymentStatus } from 'constants/pay.constants';
import { changePaymentStatus } from 'services/pay.services';
import { ListTables } from 'theme/elements/List';
import { getFormattedNumber } from 'utils';

export default function AwaitingAuth() {
  const t = useAppSelector(selectTranslations);
  const imageUrl = useAppSelector(selectImageUrl);
  const countriesObj = useAppSelector(getCountriesObj);
  const currenciesObj = useAppSelector(getCurrenciesObj);
  const userPermission = useAppSelector(getUserPermission);
  const awaitingAuthorizationRequests = useAppSelector(getAwaitingAuthorization);
  const beneficiaries = _.keyBy(useAppSelector(getBeneficiaries), 'id');
  const userMessage = useAppSelector(getMessage);
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [action, setAction] = useState<{ type?: string; action?: string; id?: number }>({});
  const dispatch = useAppDispatch();
  const prefixFields = 'page.pending_requests.tab.awaiting_auth.';
  const prefixButton = 'page.pending_requests.awaiting_auth.$_button.text'
  const cOpenPendingRequests = useMemo(() => {
    const finalObj: {
      [key: string]: [{ [key: string]: string | number }] | [];
    } = {};
    _.keys(awaitingAuthorizationRequests).forEach((category) => {
      finalObj[category] = awaitingAuthorizationRequests[category].map((item) => {
        const obj = {};
        _.keys(item).forEach((key) => {
          switch (key) {
            case 'status':
              obj[key] = t(prefixFields + 'status.' + item[key] + '.text');
              break;
            case 'beneficiaryId':
              if (_.has(beneficiaries, item['beneficiaryId'])) {
                let bn: Beneficiary = _.get(beneficiaries, item['beneficiaryId']);
                obj['companyNickname'] = bn?.nickname;
                obj['companyCountry'] = _.get(countriesObj, bn?.countryId || '')?.name;
              }
              break;
            case 'createdBy':
              obj[key] = item[key]?.firstName + ' ' + item[key]?.lastName;
              break;
            case 'type':
              obj[key] = item[key];
              obj['paymentType'] = item[key];
              break;
            case 'country':
              obj[key] = countriesObj[item[key]]?.name;
              break;
            case 'currency':
              obj[key] = currenciesObj[item[key]]?.code;
              break;
            case 'amount':
              let symbol = _.has(item, 'currencyId')
                ? currenciesObj[item['currencyId']]?.symbol
                : _.isEqual(category, 'balancesRequests')
                  ? currenciesObj['16']?.symbol
                  : ''; //balance can be only shekels
              obj[key] = `${symbol}${getFormattedNumber(item[key])}`;
              break;
            default:
              obj[key] = item[key];
          }
        });
        return obj;
      });
    });
    return finalObj;
  }, [awaitingAuthorizationRequests]);

  const awaitingAuthRequestsCount = useMemo(() => {
    return _.flatten(_.values(awaitingAuthorizationRequests)).length;
  }, [awaitingAuthorizationRequests]);

  const balanceRequestsColumns = () => {
    let columns: columnType[] = [];
    _.keys(BalanceRequestTableColumns).forEach((element) => {
      let props: columnType = {
        fieldId: 'id',
        name: element,
        title: t(prefixFields + 'column.' + _.snakeCase(element) + '.text'),
        type: BalanceRequestTableColumns[element],
      };
      columns.push(props);
    });
    return columns;
  };
  const bankAccountRequestsColumns = () => {
    let columns: columnType[] = [];
    _.keys(BankAccountRequestTableColumns).forEach((element) => {
      let props: columnType = {
        fieldId: 'id',
        name: element,
        title: t(prefixFields + 'column.' + _.snakeCase(element) + '.text'),
        type: BankAccountRequestTableColumns[element],
      };
      columns.push(props);
    });
    return columns;
  };

  const beneficiaryRequestsColumns = () => {
    let columns: columnType[] = [];
    _.keys(BeneficiaryRequestTableColumns).forEach((element) => {
      let props: columnType = {
        fieldId: 'id',
        name: element,
        title: t(prefixFields + 'column.' + _.snakeCase(element) + '.text'),
        type: BeneficiaryRequestTableColumns[element],
      };
      switch (element) {
        case 'referenceNumber':
          props.eventsHandle = {
            onClick: (event) => {
              event.stopPropagation();
              const target = event.currentTarget as HTMLElement;
              navigate(`/${pagesEnum.BENEFICIARY}/details/${target.id}`);
            },
          };
          break;
      }
      columns.push(props);
    });
    return columns;
  };

  const paymentRequestsColumns = () => {
    let columns: columnType[] = [];
    _.keys(RequsetPaymentTableColumn).forEach((element) => {
      let elementName;
      switch (element) {
        case 'reference':
          elementName = 'referenceNumber';
          break;
        case 'companyNickname':
          elementName = 'companyName';
          break;
        case 'companyCountry':
          elementName = 'country';
          break;
        default:
          elementName = element;
      }
      let props: columnType = {
        fieldId: 'id',
        name: element,
        title: t(prefixFields + 'column.' + _.snakeCase(elementName) + '.text'),
        type: RequsetPaymentTableColumn[element],
      };

      switch (element) {
        case 'reference':
          props.eventsHandle = {
            onClick: (event) => {
              event.stopPropagation();
              const target = event.currentTarget as HTMLElement;
              navigate(`/${pagesEnum.REQUEST}/requestDetailsPY/${target.id}`);
            },
          };
          break;
      }
      columns.push(props);
    });
    return columns;
  };

  const [
    showBalancePendingRequests,
    showBankAccountPendingRequests,
    showBeneficiaryPendingRequests,
    showPaymentPendingRequests,
  ] = useMemo(() => {
    try {
      return [
        cOpenPendingRequests?.balancesRequests.length > 0,
        cOpenPendingRequests?.bankAccountsRequests.length > 0,
        cOpenPendingRequests?.beneficiariesRequests.length > 0,
        cOpenPendingRequests?.paymentRequests.length > 0,
      ];
    } catch (e) {
      return [false, false, false, false];
    }
  }, [cOpenPendingRequests]);
  return (
    <>
      <Collapse in={userMessage.exist}>
        <CenteredNarrowContent>
          <CustomAlert severity={userMessage.type} text={userMessage.text}></CustomAlert>
        </CenteredNarrowContent>
      </Collapse>
      {awaitingAuthRequestsCount === 0 ? (
        <CreateMessage
          noAction
          title={t(prefixFields + 'empty.title')}
          text={t(prefixFields + 'empty.subtitle')}
          imageKey={`${prefixFields}empty.image`}
        />
      ) : (
        <ListTables spacing={4}>
          {showBalancePendingRequests && (
            <Stack spacing={1}>
              <HeaderPage2 title={t(prefixFields + 'balance.title')} />
              <BasicTable
                data={cOpenPendingRequests.balancesRequests}
                columns={balanceRequestsColumns()}
                defaultSortBy="date"
                noPagination
                noSlice
              />
            </Stack>
          )}
          {showPaymentPendingRequests && (
            <Stack spacing={1}>
              <HeaderPage2 title={t(prefixFields + 'payment.title')} />
              <BasicTable
                actionsCell={
                  _.includes([UserPermission.SIGNER], userPermission)
                    ? [
                      {
                        element: (
                          <Button variant='contained' size='small'>{t(prefixButton.replace('$', 'approve_payment'))}</Button>
                        ),
                        onClick: (row) => {
                          navigate(`/${pagesEnum.PAY}/edit/${row.id}`);
                          // setAction({
                          // 	type: PendingRequestTypes.PAYMENTS,
                          // 	action: 'approve',
                          // 	id: row.id,
                          // });
                          setOpenDialog(true);
                        },
                      },
                      {
                        element: (
                          <Button variant='contained' size='small'>{t(prefixButton.replace('$', 'reject_payment'))}</Button>
                        ),
                        onClick: (row) => {
                          setAction({
                            type: PendingRequestTypes.PAYMENTS,
                            action: 'reject',
                            id: row.id,
                          });
                          setOpenDialog(true);
                        },
                      },
                    ]
                    : []
                }
                data={cOpenPendingRequests.paymentRequests}
                columns={paymentRequestsColumns()}
                defaultSortBy="createdAt"
                rowsClass="hoverButton"
                noPagination
                noSlice
              />
            </Stack>
          )}
          {showBeneficiaryPendingRequests && (
            <Stack spacing={1}>
              <HeaderPage2 title={t(prefixFields + 'beneficiary.title')} />
              <BasicTable
                actionsCell={
                  _.includes([UserPermission.SIGNER], userPermission)
                    ? [
                      {
                        element: (
                          <Button variant='contained' size='small'>{t(prefixButton.replace('$', 'approve_beneficiary'))}</Button>
                        ),
                        onClick: (row) => {
                          navigate(`/${pagesEnum.BENEFICIARY}/edit/${row.id}`);
                          // setAction({
                          // 	type: PendingRequestTypes.BENEFICIARIES,
                          // 	action: 'approve',
                          // 	id: row.id,
                          // });
                          // setOpenDialog(true);
                        },
                      },
                      {
                        element: (
                          <Button variant='contained' size='small'>{t(prefixButton.replace('$', 'reject_beneficiary'))}</Button>
                        ),
                        onClick: (row) => {
                          setAction({
                            type: PendingRequestTypes.BENEFICIARIES,
                            action: 'reject',
                            id: row.id,
                          });
                          setOpenDialog(true);
                        },
                      },
                    ]
                    : []
                }
                data={cOpenPendingRequests.beneficiariesRequests}
                columns={beneficiaryRequestsColumns()}
                defaultSortBy="date"
                rowsClass="hoverButton"
                noPagination
                noSlice
              />
            </Stack>
          )}
          {showBankAccountPendingRequests && (
            <Stack spacing={1}>
              <HeaderPage2 title={t(prefixFields + 'bank_account.title')} />
              <BasicTable
                data={cOpenPendingRequests.bankAccountsRequests}
                columns={bankAccountRequestsColumns()}
                defaultSortBy="date"
                noPagination
                noSlice
              />
            </Stack>
          )}
        </ListTables>
      )}
      {action?.action === 'reject' && (
        <WarningDialog
          open={openDialog}
          title={t('page.pending_requests.awaiting_auth.decline_popup.header.text')}
          subTitle={'page.pending_requests.awaiting_auth.decline_popup.header.description'}
          cancelLabel={t('page.pending_requests.awaiting_auth.decline_popup.approve_button.text')}
          continueLabel={t('page.pending_requests.awaiting_auth.decline_popup.cancel_button.text')}
          handleCancel={() => {
            setOpenDialog(false);
            setAction({});
          }}
          handleContinue={() => {
            setOpenDialog(false);
            if (action.type === PendingRequestTypes.PAYMENTS) {
              if (action.id) {
                const state =
                  action?.action === 'approve' ? PaymentStatus.Approved : PaymentStatus.Deleted;
                dispatch(changePaymentStatus(action.id, state));
              }
            } else {
              //beneficiary
              if (action.id) {
                const state =
                  action?.action === 'approve'
                    ? BeneficiaryStatus.Approved
                    : BeneficiaryStatus.Declined;
                dispatch(changeBeneficiaryStatus(action.id, state));
              }
            }
          }}
        />
      )}
    </>
  );
}
